import { localizeApiTitlePayload } from "~/server/media-payload.server"
import useLocale from "~/utils/locale"
import { mediaLanguageUrl } from "~/utils/media-presentation"
import {
	type LoaderFunction,
	type LoaderFunctionArgs,
	json,
} from "@remix-run/node"
import { useQuery } from "@tanstack/react-query"
import type {
	MoviesInCollection,
	MovieCollectionParams,
} from "~/server/collection.server"
import { getMoviesInCollection } from "~/server/collection.server"
import type { MovieDetails } from "~/server/details.server"
import {
	TITLE_EXTRAS_STALE_TIME,
	movieCollectionQueryKey,
} from "~/utils/title-extras"

type LoaderData = {
	collectionId: string
	movies: MovieDetails[]
}

export const loader: LoaderFunction = async ({
	request,
}: LoaderFunctionArgs) => {
	const url = new URL(request.url)
	const collectionId = url.searchParams.get("collectionId") || ""
	const movieIds = url.searchParams.get("movieIds") || ""
	const moviesInCollection = await getMoviesInCollection({
		collectionId,
		movieIds,
	})

	return json<LoaderData>(await localizeApiTitlePayload(moviesInCollection, request))
}

export const useMovieCollection = ({
	collectionId,
	movieIds,
}: MovieCollectionParams) => {
	const { locale } = useLocale()
	return useQuery<MoviesInCollection>({
		queryKey: movieCollectionQueryKey(collectionId, movieIds, locale.language),
		enabled: Boolean(collectionId && movieIds),
		// The movies come with the document; see TITLE_EXTRAS_STALE_TIME for the reason.
		staleTime: TITLE_EXTRAS_STALE_TIME,
		queryFn: async ({ signal }) => {
			const params = new URLSearchParams({ collectionId, movieIds })
			const response = await fetch(mediaLanguageUrl(`/api/movie/collection?${params}`, locale.language), {
				signal,
			})
			if (!response.ok) throw new Error("Could not load movie collection")
			return response.json()
		},
	})

}
